import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import EngineerLayout from './EngineerLayout';
import { 
    FaArrowLeft, 
    FaSort,
    FaPrint,
    FaFileExcel,
    FaCopy
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const EngineerSocietyWorkReportList = () => {
    const navigate = useNavigate();
    
    const [works, setWorks] = useState([]);
    const [selectedTaluka, setSelectedTaluka] = useState('');
    const [appliedTaluka, setAppliedTaluka] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Hardcoded Marathi Taluka List for Sangli district
    const talukaList = [
        { en: 'Shirala', mr: 'शिराळा' },
        { en: 'Walwa', mr: 'वाळवा' },
        { en: 'Tasgaon', mr: 'तासगाव' },
        { en: 'Khanapur', mr: 'खानापूर (विटा)' },
        { en: 'Atpadi', mr: 'आटपाडी' },
        { en: 'Kavathe Mahankal', mr: 'कवठे महांकाळ' },
        { en: 'Miraj', mr: 'मिरज' },
        { en: 'Palus', mr: 'पलूस' },
        { en: 'Jat', mr: 'जत' },
        { en: 'Kadegaon', mr: 'कडेगाव' },
        { en: 'Mukhyalaya', mr: 'मुख्यालय' }
    ];

    useEffect(() => {
        setLoading(true);

        const generateWorkRef = ref(db, 'ekamwatapzp/generatework');
        onValue(generateWorkRef, (snapshot) => {
            if (snapshot.exists()) {
                const datesData = snapshot.val();
                let allFlattenedWorks = [];

                Object.values(datesData).forEach(dateSnapshot => {
                    const traverseNode = (node) => {
                        if (!node || typeof node !== 'object') return;
                        
                        if (node.schemeName || node.workName) {
                            allFlattenedWorks.push({
                                ...node,
                                id: node.id || node.workNumber || Math.random().toString(36).substr(2, 9)
                            });
                        } else {
                            Object.values(node).forEach(child => traverseNode(child));
                        }
                    };

                    traverseNode(dateSnapshot);
                });

                // Filter for Societies
                const filteredByType = allFlattenedWorks.filter(work => {
                    const isWorkSociety = (work.category || '').toLowerCase().includes('society') || 
                                         (work.registrationType || '').toLowerCase().includes('society') ||
                                         (work.sharedwork || '').includes('मजूर');
                    return isWorkSociety;
                });

                const uniqueWorks = [];
                const seenNumbers = new Set();
                filteredByType.forEach(w => {
                    if (w.workNumber && !seenNumbers.has(w.workNumber)) {
                        uniqueWorks.push(w);
                        seenNumbers.add(w.workNumber);
                    } else if (!w.workNumber) {
                        uniqueWorks.push(w);
                    }
                });

                setWorks(uniqueWorks);
            }
            setLoading(false);
        });

    }, []);

    const handleSearch = () => {
        setAppliedTaluka(selectedTaluka);
    };

    const normalizeTaluka = (name) => {
        if (!name) return "";
        const n = name.toString().toLowerCase().trim();
        const standardMap = {
            'shirala': 'shirala', 'शिराळा': 'shirala',
            'walwa': 'walwa', 'वाळवा': 'walwa', 'islampur': 'walwa',
            'tasgaon': 'tasgaon', 'तासगाव': 'tasgaon',
            'khanapur': 'khanapur', 'खानापूर': 'khanapur', 'vita': 'khanapur', 'खानापूर (विटा)': 'khanapur',
            'atpadi': 'atpadi', 'आटपाडी': 'atpadi',
            'kavathe mahankal': 'kavathe mahankal', 'कवठे महांकाळ': 'kavathe mahankal', 'kavathemahangal': 'kavathe mahankal', 'कवठेमहांकाळ': 'kavathe mahankal',
            'miraj': 'miraj', 'मिरज': 'miraj', 'sangli': 'miraj',
            'palus': 'palus', 'पलूस': 'palus',
            'jat': 'jat', 'जत': 'jat', 'jath': 'jat',
            'kadegaon': 'kadegaon', 'कडेगाव': 'kadegaon',
            'mukhyalaya': 'mukhyalaya', 'मुख्यालय': 'mukhyalaya'
        };
        return standardMap[n] || n;
    };

    const getMarathiTaluka = (enName) => {
        if (!enName) return "-";
        const normalizedEn = normalizeTaluka(enName);
        const found = talukaList.find(t => normalizeTaluka(t.en) === normalizedEn);
        return found ? found.mr : enName;
    };

    const displayData = useMemo(() => {
        let filtered = works;
        if (appliedTaluka) {
            const standardizedApplied = normalizeTaluka(appliedTaluka);
            filtered = filtered.filter(w => normalizeTaluka(w.taluka) === standardizedApplied);
        }
        if (searchTerm) {
            filtered = filtered.filter(w => 
                (w.workName && w.workName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (w.workNumber && w.workNumber.toString().includes(searchTerm)) ||
                (w.schemeName && w.schemeName.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        return filtered;
    }, [works, appliedTaluka, searchTerm]);

    const downloadExcel = () => {
        const excelData = displayData.map((w, i) => ({
            'अ.क्र.': i + 1,
            'वर्क नंबर': w.workNumber || "-",
            'तालुका': getMarathiTaluka(w.taluka),
            'कामाचे नाव': w.workName,
            'कामाची मुदत': w.workDuration || "3 महिने",
            'प्रशासकीय मंजुरी': (w.inwardNo || w.aaNo || "-") + " " + (w.date || w.aaDate || "-"),
            'तांत्रिक मंजुरी': (w.technicalApprovalOrderNo || w.tsNo || "-") + " " + (w.inwardDate || w.tsDate || "-"),
            'रक्कम (लाखात)': w.tenderAmount || w.amount,
            'योजनेचे नाव': w.schemeName
        }));
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Society_Work_Report");
        XLSX.writeFile(wb, "Engineer_Society_Work_Report.xlsx");
        toast.success("एक्सेल फाईल डाउनलोड झाली!");
    };

    const copyToClipboard = () => {
        const content = displayData.map((w, i) => 
            `${i+1}\t${w.workNumber}\t${getMarathiTaluka(w.taluka)}\t${w.workName}\t${w.amount}`
        ).join('\n');
        navigator.clipboard.writeText(content);
        toast.success("माहिती कॉपी झाली!");
    };

    if (loading) {
        return (
            <EngineerLayout>
                <div className="p-20 flex flex-col items-center justify-center space-y-4 font-marathi">
                    <div className="animate-spin text-[#4e73df] text-4xl border-4 border-t-transparent border-gray-200 rounded-full w-12 h-12"></div>
                    <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs">माहिती लोड होत आहे...</p>
                </div>
            </EngineerLayout>
        );
    }

    return (
        <EngineerLayout>
            <div className="p-4 md:p-8 bg-[#f8f9fc] min-h-screen font-marathi text-gray-800">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 px-2 no-print">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate(-1)}
                            className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#4e73df] transition active:scale-95"
                        >
                            <FaArrowLeft />
                        </button>
                        <h1 className="text-2xl font-bold text-gray-800 tracking-tight">मजूर रिपोर्ट</h1>
                    </div>
                    <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400 uppercase tracking-wider">
                        जिल्हा परिषद / <span className="text-gray-600 font-black">मजूर रिपोर्ट</span>
                    </div>
                </div>

                <div className="bg-white rounded shadow-sm border border-gray-200 p-8 mb-8 no-print">
                    <div className="flex flex-col gap-3">
                        <label className="text-sm font-bold text-gray-700">तालुका निवडा <span className="text-red-500">*</span></label>
                        <div className="flex flex-col md:flex-row gap-4 max-w-2xl">
                            <select 
                                className="flex-grow bg-white border border-gray-200 rounded py-2 px-4 focus:ring-2 focus:ring-blue-500/10 outline-none transition font-medium"
                                value={selectedTaluka}
                                onChange={(e) => setSelectedTaluka(e.target.value)}
                            >
                                <option value="">तालुका निवडा</option>
                                {talukaList.map(t => (
                                    <option key={t.en} value={t.en}>{t.mr}</option>
                                ))}
                            </select>
                            <button 
                                onClick={handleSearch}
                                className="bg-[#4e73df] hover:bg-blue-700 text-white px-8 py-2 rounded font-bold shadow-md transition transform active:scale-95 whitespace-nowrap"
                            >
                                शोध
                            </button>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-t border border-gray-200 p-4 border-b-0 flex flex-col md:flex-row justify-between items-center gap-4 no-print mt-4">
                    <div className="flex items-center gap-1">
                        <button onClick={copyToClipboard} className="bg-[#6c757d] text-white px-4 py-1.5 rounded text-sm font-bold hover:bg-gray-700 shadow-sm flex items-center gap-2">
                            <FaCopy size={13} /> कॉपी करा
                        </button>
                        <button onClick={downloadExcel} className="bg-[#6c757d] text-white px-4 py-1.5 rounded text-sm font-bold hover:bg-gray-700 shadow-sm flex items-center gap-2">
                            <FaFileExcel size={13} /> एक्सेल
                        </button>
                        <button onClick={() => window.print()} className="bg-[#6c757d] text-white px-4 py-1.5 rounded text-sm font-bold hover:bg-gray-700 shadow-sm flex items-center gap-2">
                            <FaPrint size={13} /> प्रिंट
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-600">शोधा:</span>
                        <input 
                            type="text"
                            className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="bg-white rounded-b shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse border border-gray-200 font-marathi">
                            <thead className="bg-[#f8f9fa] text-center">
                                <tr><th colSpan="10" className="px-4 py-3 border-b border-gray-200 text-lg font-bold text-gray-800">बांधकाम उपविभाग पंचायत समिती</th></tr>
                                <tr><th colSpan="10" className="px-4 py-5 border-b border-gray-200 text-sm font-bold text-gray-600">जिल्हा परिषद काम वाटप समिती पुढे शिफारस करावयाच्या यादी दि. {appliedTaluka ? new Date().toLocaleDateString('mr-IN') : "________"} रोजी घ्यावयाची कामे</th></tr>
                                <tr className="text-[11px] font-black text-gray-500 uppercase">
                                    <th className="px-2 py-4 border border-gray-200 w-12">अ.क्र.</th>
                                    <th className="px-2 py-4 border border-gray-200 w-16">वर्क नंबर</th>
                                    <th className="px-2 py-4 border border-gray-200 w-24">तालुका</th>
                                    <th className="px-2 py-4 border border-gray-200 min-w-[250px]">कामाचे नाव</th>
                                    <th className="px-2 py-4 border border-gray-200 w-24">मुदत (महिने)</th>
                                    <th className="px-2 py-4 border border-gray-200 w-32">प्रशासकीय मंजुरी</th>
                                    <th className="px-2 py-4 border border-gray-200 w-32">तांत्रिक मंजुरी</th>
                                    <th className="px-2 py-4 border border-gray-200 w-24 text-center">र.रु. (लाखात)</th>
                                    <th className="px-2 py-4 border border-gray-200 min-w-[180px]">योजनेचे नाव</th>
                                    <th className="px-2 py-4 border border-gray-200 w-24">शेरा</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-[12px]">
                                {displayData.map((item, index) => (
                                    <tr key={index} className="hover:bg-blue-50/10 transition-colors">
                                        <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-400">{index + 1}</td>
                                        <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-700">{item.workNumber || "-"}</td>
                                        <td className="px-2 py-4 border border-gray-200 font-bold text-gray-700">{getMarathiTaluka(item.taluka)}</td>
                                        <td className="px-2 py-4 border border-gray-200 font-bold text-gray-800 leading-relaxed max-w-[300px]">{item.workName}</td>
                                        <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-600">{item.workDuration || "3 महिने"}</td>
                                        <td className="px-2 py-4 border border-gray-200 text-center font-bold text-[11px]">
                                            <div>{item.inwardNo || item.aaNo || "-"}</div>
                                            <div className="text-gray-400">{item.date || item.aaDate || "-"}</div>
                                        </td>
                                        <td className="px-2 py-4 border border-gray-200 text-center font-bold text-[11px]">
                                            <div>{item.technicalApprovalOrderNo || item.tsNo || "-"}</div>
                                            <div className="text-gray-400">{item.inwardDate || item.tsDate || "-"}</div>
                                        </td>
                                        <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-700">
                                            {item.tenderAmount || item.amount}
                                        </td>
                                        <td className="px-2 py-4 border border-gray-200 font-bold text-gray-600 leading-tight">{item.schemeName}</td>
                                        <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-300">-</td>
                                    </tr>
                                ))}
                                {displayData.length === 0 && (
                                    <tr><td colSpan="10" className="px-6 py-24 text-center text-gray-300 font-bold italic bg-gray-50/30">तपशील उपलब्ध नाही.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <style>{`
                .font-marathi { font-family: 'Hind', sans-serif; }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; }
                    .shadow-sm { box-shadow: none !important; border: 1px solid #eee !important; }
                    table { font-size: 10px !important; }
                }
            `}</style>
        </EngineerLayout>
    );
};

export default EngineerSocietyWorkReportList;
